import { useState } from "react";
import { FileUploadConfig, getSupportedExtensions } from "@/fileUpload/fileUploadDomain";
import { Button, ButtonProps, LinkButton } from "./Button";
import { useDispatch, useTranslation } from "@/stores/RootStoreContext";
import { TMessage } from "@/i18n/tt";
import { DCEvent } from "@/events/eventsDomain";

export type SelectFilesButtonProps = {
  fileUploadConfig: FileUploadConfig;
  onChange: SelectFilesInputProps["onChange"];
  variant?: ButtonProps["variant"] | "link";
  icon?: ButtonProps["icon"];
  label?: TMessage | string | false;
  tooltip?: ButtonProps["tooltip"];
  fullWidth?: ButtonProps["fullWidth"];
  size?: ButtonProps["size"];
  isLoading?: boolean;
  isDisabled?: boolean;
  multiple?: boolean;
  className?: string;
  setInputRef?: (el: HTMLInputElement | null) => void;
  inputId?: string;
};

export type SelectFilesInputProps = {
  fileUploadConfig: FileUploadConfig;
  onChange: (files: File[], fileUploadConfig: FileUploadConfig) => DCEvent[] | undefined | void;
  isDisabled?: boolean;
  multiple?: boolean;
  setInputRef?: (el: HTMLInputElement | null) => void;
  inputId?: string;
};

export function SelectFilesInput({
  fileUploadConfig,
  onChange,
  isDisabled,
  multiple = true,
  setInputRef,
  inputId,
}: SelectFilesInputProps) {
  const dispatch = useDispatch();
  return (
    <input
      disabled={isDisabled}
      id={inputId}
      type="file"
      ref={setInputRef}
      className={"hidden"}
      multiple={multiple}
      accept={getSupportedExtensions(fileUploadConfig.supportedFileTypes, {
        leadingDot: true,
      }).toString()}
      onChange={(event) => {
        const events = onChange(
          event.target.files ? Array.from(event.target.files) : [],
          fileUploadConfig,
        );
        if (events) {
          dispatch(events);
        }
      }}
      onClick={(event) => {
        (event.target as HTMLInputElement).value = "";
      }}
    />
  );
}

export function SelectFilesButton(props: SelectFilesButtonProps) {
  const { t } = useTranslation();
  const [filesInputRef, setRef] = useState<HTMLInputElement | null>(null);
  const { variant, icon, fullWidth, isLoading, isDisabled, multiple = true, className } = props;
  const defaultLabel = t(multiple ? "select-files" : "select-file");
  const label = props.label === false ? undefined : (props.label ?? defaultLabel);
  const tooltip = props.tooltip ?? (label ? undefined : defaultLabel);

  return (
    <>
      <SelectFilesInput {...props} setInputRef={setRef} />
      {variant === "link" ? (
        <LinkButton
          icon={icon ?? "folder"}
          label={label}
          tooltip={tooltip}
          onClick={() => filesInputRef?.click()}
          loading={isLoading}
          disabled={isDisabled}
          className={className}
        />
      ) : (
        <Button
          variant={variant ?? "secondary"}
          icon={icon ?? "folder"}
          label={label}
          tooltip={tooltip}
          onClick={() => filesInputRef?.click()}
          loading={isLoading}
          disabled={isDisabled}
          className={className}
          fullWidth={fullWidth}
          size={props.size}
        />
      )}
    </>
  );
}
